import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Sprint } from '../../types';
import {
  getProjectSprints, createSprint, updateSprint, demoteOtherActiveSprints,
  SprintStatus, SPRINT_STATUS_LABEL,
} from '../../services/sprintService';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BM_LABEL, BM_INPUT, BM_TITLE_INPUT, BM_TEXTAREA,
} from '../BoardModal';

const STATUS_BADGE: Record<number, { bg: string; fg: string }> = {
  [SprintStatus.Planned]:   { bg: '#F3F4F6', fg: '#5A6272' },
  [SprintStatus.Active]:    { bg: '#D4EEF5', fg: '#0A5568' },
  [SprintStatus.Completed]: { bg: '#D1FAE5', fg: '#065F46' },
  [SprintStatus.Archived]:  { bg: '#F3F4F6', fg: '#9198A8' },
};

function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SprintsSection({ pca, projectId }: { pca: IPublicClientApplication; projectId: string }) {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Sprint | 'new' | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    return getProjectSprints(pca, projectId).then(setSprints).finally(() => setLoading(false));
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="eyebrow">SPRINTS</span>
        <button type="button" className="btn-secondary" onClick={() => setEditing('new')}>+ New Sprint</button>
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
      ) : sprints.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '8px 0' }}>No sprints yet. Create one to start planning.</div>
      ) : (
        <div>
          {sprints.map(s => {
            const active = s.dagrun_pm_status === SprintStatus.Active;
            const badge = STATUS_BADGE[s.dagrun_pm_status] ?? STATUS_BADGE[SprintStatus.Planned];
            return (
              <div key={s.dagrun_pm_sprintid} onClick={() => setEditing(s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', marginBottom: 6,
                  border: '1px solid var(--border-light)', borderLeft: `3px solid ${active ? '#0E7490' : 'transparent'}`,
                  borderRadius: 6, cursor: 'pointer', background: active ? 'rgba(14,116,144,0.04)' : '#fff',
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{s.dagrun_pm_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
                    {fmt(s.dagrun_pm_startdate)} – {fmt(s.dagrun_pm_enddate)}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                  {s.dagrun_pm_pointscompleted ?? 0}/{s.dagrun_pm_pointsplanned ?? 0} pts
                </span>
                <span style={{ ...badge, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>
                  {SPRINT_STATUS_LABEL[s.dagrun_pm_status] ?? '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <SprintEditor pca={pca} projectId={projectId}
          sprint={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function SprintEditor({ pca, projectId, sprint, onClose, onSaved }: {
  pca: IPublicClientApplication; projectId: string; sprint: Sprint | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(sprint?.dagrun_pm_name ?? '');
  const [goal, setGoal] = useState(sprint?.dagrun_pm_sprintgoal ?? '');
  const [start, setStart] = useState(sprint?.dagrun_pm_startdate?.slice(0, 10) ?? '');
  const [end, setEnd] = useState(sprint?.dagrun_pm_enddate?.slice(0, 10) ?? '');
  const [status, setStatus] = useState<number>(sprint?.dagrun_pm_status ?? SprintStatus.Planned);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !start || !end || saving) return;
    setSaving(true); setError(null);
    try {
      const startIso = new Date(start).toISOString();
      const endIso = new Date(end).toISOString();
      // Enforce single Active sprint per project.
      if (status === SprintStatus.Active) {
        await demoteOtherActiveSprints(pca, projectId, sprint?.dagrun_pm_sprintid ?? null);
      }
      if (sprint) {
        await updateSprint(pca, sprint.dagrun_pm_sprintid, {
          dagrun_pm_name: name.trim(), dagrun_pm_sprintgoal: goal.trim() || null,
          dagrun_pm_startdate: startIso, dagrun_pm_enddate: endIso, dagrun_pm_status: status,
        });
      } else {
        const id = await createSprint(pca, projectId, name.trim(), goal.trim(), startIso, endIso);
        if (status !== SprintStatus.Planned) await updateSprint(pca, id, { dagrun_pm_status: status });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title={sprint ? 'Edit Sprint' : 'New Sprint'} onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim() || !start || !end}>{saving ? 'Saving…' : 'Save Sprint'}</BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Name *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Goal</label>
        <textarea value={goal} onChange={e => setGoal(e.target.value)} style={BM_TEXTAREA} placeholder="What is this sprint aiming to deliver?" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Start Date *</label>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>End Date *</label>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      <div style={{ marginBottom: 4 }}>
        <label style={BM_LABEL}>Status</label>
        <select value={status} onChange={e => setStatus(Number(e.target.value))} style={BM_INPUT}>
          {Object.entries(SPRINT_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
